import { useEffect, useState, useCallback } from 'react'
import { useApp } from '../context/AppContext'
import { sileo } from 'sileo'

/**
 * Custom hook to retrieve and manage a user by ID, including:
 * - KPIS user data (subscription, supervisor status)
 * - Employee data from the public schema
 *
 * @param {string|number} id - Identifier of the user (Auth ID or UserMainId).
 * @param {string} [empField='id_user'] - Field type used to resolve the user ('Auth' or default).
 *
 * @returns {Object} User state and handlers.
 * @returns {Object|null} returns.employee - Employee data from the database.
 * @returns {boolean} returns.isSubscribed - Indicates if the user exists in KPIS.
 * @returns {boolean} returns.loading - Indicates whether data is being loaded.
 * @returns {number|null} returns.kpisUserId - KPIS user ID.
 * @returns {boolean} returns.isSupervisor - Indicates if the user is a supervisor.
 * @returns {Function} returns.changeSupervisor - Updates supervisor status.
 */
const useUser = (id, empField = 'id_user') => {
  const { supabase } = useApp()

  const [isSupervisor, setIsSupervisor] = useState(false)
  const [employee, setEmployee] = useState(null)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [loading, setLoading] = useState(true)
  const [kpisUserId, setKpisUserId] = useState(null)

  /**
   * Fetches user data from KPIS and employee data from the public schema.
   * Handles both Auth-based and direct ID lookups.
   */
  useEffect(() => {
    if (!id) {
      setLoading(false)
      return
    }

    let isMounted = true

    const fetchUser = async () => {
      setLoading(true)

      try {
        const column = empField === 'Auth' ? 'AuthId' : 'UserMainId'

        // -------------------------------
        // Query KPIS.User
        // -------------------------------
        const { data: kpisUser, error: kpisError } = await supabase
          .schema('KPIS')
          .from('User')
          .select('*')
          .eq(column, id)
          .maybeSingle()

        if (kpisError) {
          console.error('KPIS error:', kpisError)
          sileo.error('Error cargando usuario KPIS')
        }

        if (!isMounted) return

        const exists = !!kpisUser

        setIsSubscribed(exists)
        setKpisUserId(kpisUser?.Id ?? null)
        setIsSupervisor(kpisUser?.IsSupervisor ?? false)

        // -------------------------------
        // Resolve employee ID
        // -------------------------------
        const consultId =
          empField === 'Auth' ? (kpisUser?.UserMainId ?? null) : id

        if (!consultId) {
          setEmployee(null)
          return
        }

        // -------------------------------
        // Query Employee
        // -------------------------------
        const { data: empData, error: empError } = await supabase
          .schema('public')
          .from('Empleados')
          .select('*')
          .eq('id', consultId)
          .maybeSingle()

        if (empError) {
          console.error('Empleado error:', empError)
          sileo.error({
            title: 'Error cargando empleado',
          })
          setEmployee(null)
        } else {
          setEmployee(empData ?? null)
        }
      } catch (err) {
        console.error('Unexpected error:', err)
        sileo.error({
          title: 'Error inesperado',
        })
        setEmployee(null)
      } finally {
        if (isMounted) setLoading(false)
      }
    }

    fetchUser()

    return () => {
      isMounted = false
    }
  }, [id, empField, supabase])

  /**
   * Updates the supervisor status of the current user in KPIS.
   * Includes optimistic UI update with rollback on failure.
   *
   * @param {boolean} state - New supervisor state.
   */
  const changeSupervisor = useCallback(
    async (state) => {
      if (!isSubscribed || !kpisUserId) {
        sileo.error({
          title: 'Usuario no existe en KPIS',
        })
        return
      }

      const previous = isSupervisor

      try {
        setIsSupervisor(state)

        const { data, error } = await supabase
          .schema('KPIS')
          .from('User')
          .update({ IsSupervisor: state })
          .eq('Id', kpisUserId)
          .select()
          .single()

        if (error) throw error

        if (!data) {
          throw new Error('No se actualizó ningún registro')
        }

        setIsSupervisor(data.IsSupervisor)
      } catch (error) {
        console.error('Update error:', error)

        // Rollback UI state
        setIsSupervisor(previous)

        sileo.error({
          title: 'Error actualizando supervisor',
          description: error.message,
        })
      }
    },
    [isSubscribed, kpisUserId, isSupervisor, supabase]
  )

  return {
    employee,
    isSubscribed,
    loading,
    kpisUserId,
    isSupervisor,
    changeSupervisor,
  }
}

export default useUser
